import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Footprints, Bus, Bike, Navigation, Accessibility } from "lucide-react";
import { api } from "@/lib/api";
import { formatMiles } from "@/lib/time";

export default function DirectionsModal({ open, onClose, leg, from, to, accessible }) {
  const [data, setData] = useState(null);
  const [busEta, setBusEta] = useState(null);
  const mode = leg?.travel?.recommended === "bike" ? "bike" : "walk";

  useEffect(() => {
    if (!open || !from || !to) return;
    setData(null);
    setBusEta(null);
    api
      .get(`/directions?from_lat=${from[0]}&from_lng=${from[1]}&to_lat=${to[0]}&to_lng=${to[1]}&mode=${mode}&accessible=${accessible}`)
      .then((r) => setData(r.data));
    const stopId = leg?.travel?.bus_from_id;
    if (stopId) api.get(`/bus/eta?stop_id=${stopId}`).then((r) => setBusEta(r.data));
  }, [open, from, to, accessible, leg, mode]);

  const ModeIcon = mode === "bike" ? Bike : Footprints;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[2000] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={onClose}
          data-testid="directions-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl border border-border overflow-hidden max-h-[85vh] flex flex-col"
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div>
                <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Directions</div>
                <h3 className="font-display text-lg font-semibold">{leg?.from} → {leg?.to}</h3>
              </div>
              <button data-testid="directions-close" onClick={onClose} className="p-2 rounded-md hover:bg-secondary transition-colors">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              {!data ? (
                <div className="mono text-xs text-muted-foreground animate-pulse py-6 text-center">Routing…</div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`flex items-center gap-1.5 text-white rounded-md px-3 py-2 mono text-sm font-bold ${mode === "bike" ? "bg-[#0EA5E9]" : "bg-brand"}`}>
                      <ModeIcon size={15} strokeWidth={2} /> {data.eta_min} min
                    </div>
                    <span className="text-sm text-muted-foreground">{formatMiles(data.total_m)}</span>
                    {data.accessible && (
                      <span className="flex items-center gap-1 bg-[#16A34A]/10 text-[#166534] mono text-[11px] font-bold px-2 py-1 rounded">
                        <Accessibility size={12} strokeWidth={2} /> STEP-FREE
                      </span>
                    )}
                    <span className="mono text-[11px] text-muted-foreground uppercase tracking-widest ml-auto">
                      {data.estimated ? "Estimated" : "Live route"}
                    </span>
                  </div>

                  {busEta?.arrivals?.length > 0 && (
                    <div className="mb-4 rounded-lg border border-border p-3 bg-secondary/40">
                      <div className="flex items-center gap-1.5 mono text-[11px] tracking-widest uppercase text-brand mb-2">
                        <Bus size={12} strokeWidth={1.5} /> Live buses at {leg.travel.bus_from} <span className="text-muted-foreground">· simulated</span>
                      </div>
                      <div className="space-y-1.5">
                        {busEta.arrivals.map((a, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: a.color }} />
                            <span className="flex-1 font-semibold truncate">{a.route}</span>
                            <span className="mono font-bold text-brand">{a.eta_min === 0 ? "Now" : `${a.eta_min}m`}</span>
                            <span className="mono text-xs text-muted-foreground">then {a.following_min}m</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <ol className="space-y-3">
                    {data.steps.map((s, i) => (
                      <li key={i} className="flex gap-3">
                        <div className="w-7 h-7 rounded-md bg-secondary flex items-center justify-center shrink-0">
                          {i === data.steps.length - 1 ? (
                            <Navigation size={14} strokeWidth={1.5} className="text-brand" />
                          ) : (
                            <span className="mono text-xs font-bold text-brand">{i + 1}</span>
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{s.instruction}</div>
                          <div className="mono text-xs text-muted-foreground">{formatMiles(s.distance_m)}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
